import { AllowedTextSizes, AllowedWeights } from '@picsart/growth-rc/types/lib/atoms/Description';
import { TextSizes } from '@picsart/growth-rc/types/lib/atoms/Title';
import { SubscriptionTierType } from '@picsart/monetization/types';
import { BreadcrumbModel } from 'components/Breadcrumb';
import { LandingCategory } from 'constants/enums';

type Id = number;

export enum AppRoutes {
  HOME = '',
  BRANDS = 'brands',
  APPS = 'apps-jpcpg',
  NOT_FOUND = 'not-found',
  DRAFT = 'draft',
}

export interface DataMigrationModel extends PageSettingsModel {
  name: string;
  slug: string;
  seoSettings: SeoSettingsModel;
  Components: Array<MigrationComponentModel>;
}

export interface MigrationComponentBaseModel {
  __component: string;
}

export interface MigrationComponentModel extends Record<string, any>, MigrationComponentBaseModel {}

export interface SeoSettingsModel {
  id?: number;
  siteName?: string;
  noIndex?: boolean;
  ogDescription?: string;
  description?: string;
  nextPageUrl?: string;
  twitterSite?: string;
  prevPageUrl?: string;
  ogImage?: string;
  canonical?: string;
  bannerImage?: string;
  keywords?: string;
  ogTitle?: string;
  ogType?: string;
  ogUrl?: string;
  title?: string;
  metaDescription?: string;
  metaOGDescription?: string;
  canonicalUrl?: string;
}

export type LinkModel = { id: string; text: string; url: string };

export type TitleModel = {
  id: number;
  tag?: 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  size?: TextSizes;
  text?: string;
  color?: string;
};

export type DescriptionModel = {
  id: number;
  text?: string;
  size?: AllowedTextSizes;
  fontWeight?: AllowedWeights;
};

export type FooterRelevantLinksModel = {
  id: number;
  title?: TitleModel;
  description?: DescriptionModel;
  links: LinkModel[];
};

export interface PageSettingsModel extends StrapiLandingBaseModel {
  promoRequired?: boolean;
  monetizationForceTier?: SubscriptionTierType;
  monetizationEnableTiers?: boolean;
  premiumPopupPackage?: string;
  customEventName?: string;
  buttonProps?: object;
  waitForPulse: boolean;
}

export interface StrapiDataModel extends DataMigrationModel {
  id: number;
  locale: string;
  localizations: LocalizationItemModel[] | [];
  sitemap_exclude?: boolean;
  translations: Record<string, string>;
  hubs?: StrapiHubModel[];
  parentLanding?: StrapiLandingBaseModel;
  childLandings?: StrapiLandingBaseModel[];
  onlyForGuests?: boolean;
  containVideo?: boolean;
  isMaximizerMode?: boolean;
}

export enum ColorScheme {
  auto = 'auto',
  light = 'light',
  dark = 'dark',
}
export interface IBreadcrumbModel {
  name?: string;
  hide?: boolean;
  colorScheme?: ColorScheme;
  link?: string;
}

export interface ImageModel {
  id: Id;
  src: string;
  alt: string;
}

export interface StrapiHubModel {
  id?: number;
  title?: string;
  slug?: string;
  createdAt?: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  sitemap_exclude?: boolean;
  locale?: string;
  category?: string;
  customEventName?: string;
  popularOrdering?: number;
  breadcrumb?: IBreadcrumbModel;
  footerRelevantLinks?: FooterRelevantLinksModel;
  landings?: StrapiLandingBaseModel[];
}

export interface Link {
  id: Id;
  text: string;
  url: string;
}

export interface ColorModel {
  id: Id;
  name: string;
  hex: string;
  colorPage?: {
    id: Id;
    slug: string;
    locale: string;
  };
}

export interface ColorPageBannerModel {
  id: Id;
  leftTextColor: ColorScheme;
  rightTextColor: ColorScheme;
  breadCrumb: BreadcrumbModel;
  cta: Link;
  description: DescriptionModel;
  title: TitleModel;
  relatedColors: StrapiLandingBaseModel[];
}

export type ColorPageMoreItemsModel = {
  id: Id;
  link: LinkModel;
  image: ImageModel;
}[];

export type ColorDataModel = {
  category: string;
  hex: string;
  darkTheme?: boolean;
};

export interface StrapiColorPageModel {
  slug?: string;
  templatesColor?: string;
  seoSettings?: SeoSettingsModel;
  color?: ColorModel;
  banner?: ColorPageBannerModel;
  colorDescription?: {
    id: Id;
    title: TitleModel;
    description: DescriptionModel;
  };
  colorDetails?: {
    id: Id;
    title: TitleModel;
    description: DescriptionModel;
  }[];
  explore?: {
    id: Id;
    title: TitleModel;
    description: DescriptionModel;
  };
  more?: ColorPageMoreItemsModel;
}

export interface StrapiResponseModel {
  data: StrapiDataModel & StrapiHubModel & StrapiColorPageModel;
  hubs?: StrapiHubModel[];
}

export interface JsonDataModel {
  content: any;
  component: string;
}

type LocalizationItemModel = {
  id: number;
  slug: string;
  createdAt: string;
  updatedAt: string;
  locale: string;
  publishedAt: string;
  popularOrdering: string;
  name: string;
  imgSrc: string;
  imgAlt: string;
  category: string;
};

export type AppsJpcpgModel = {
  miniNavigation?: boolean;
  customFooter?: boolean;
};

export type LogoBehaviorProps = {
  logoSrc: string;
  logoLink: string;
};

export type DeviceModel = 'Android' | 'IOS' | 'Desktop' | 'Tablet' | 'Mobile';

export interface JsonResponseModel extends PageSettingsModel {
  devices?: DeviceModel[];
  imgSrc: string;
  seoSettings: SeoSettingsModel;
  components: Array<JsonDataModel>;
  localizations: LocalizationItemModel[] | [];
  translations: Record<string, string>;
  locale: string;
  miniNavigation?: boolean;
  customFooter?: boolean;
  stickyBar?: boolean;
  miniFooter?: boolean;
  hideHeaderAndFooterNavigations?: boolean;
  logoBehavior?: LogoBehaviorProps;
}

export interface StrapiLandingBaseModel {
  countries?: string[];
  Tags?: undefined | string;
  name: string;
  slug?: string;
  category?: LandingCategory;
  imgAlt?: string;
  imgSrc: string;
  monetizationRequired?: boolean;
  breadcrumb?: IBreadcrumbModel;
  footerRelevantLinks?: FooterRelevantLinksModel;
  color?: string;
  colorData?: {
    hex: string;
    category: string;
    darkTheme?: boolean;
  };
  colorPages?: StrapiLandingBaseModel[];
  locale?: string;
}

export type PageDataModel = JsonResponseModel & AppsJpcpgModel;

export interface AllLanguagesListResponseModel {
  id: number;
  name: string;
  code: string;
  createdAt?: string;
  updatedAt?: string;
  isDefault?: boolean;
}

export type ColorTags = {
  id: Id;
  name: string;
  colors: ColorModel[];
}[];
